import React, { useCallback, useEffect } from "react";
import {
  Button,
  Input,
  Modal,
  ModalActions,
  ModalBody,
  ModalHeader,
  Radio,
  RadioGroup,
  Textarea,
} from "../../wombat-ui";

import { useForm, useWatch } from "react-hook-form";

import useViewer from "../../../hooks/useViewer";

import useTranslations from "../../../hooks/useTranslations";
import useAddNotification from "../../../hooks/useAddNotification";
import useInsertCampaign from "../../../graphql/hooks/mutations/useInsertCampaign";
import { GET_VIEWER } from "../../../graphql/hooks/queries/useGetViewer";

type CampaignCreationModalProps = {
  toggled: boolean;
  onClose: () => void;
};

type CampaignFormData = {
  name: string;
  description: string;
  reminderGap: number;
  reminderEnabled: string;
};

export const CampaignCreationModal: React.FC<CampaignCreationModalProps> = ({
  toggled,
  onClose,
}) => {
  const t = useTranslations();

  const {
    company: { id: companyId, reminderGap, reminderEnabled },
  } = useViewer();

  const [addNotification] = useAddNotification();
  const [insertCampaign, { loading }] = useInsertCampaign({
    refetchQueries: [{ query: GET_VIEWER }],
  });

  const { register, handleSubmit, errors, control } = useForm<CampaignFormData>({
    defaultValues: {
      name: "",
      description: "",
      reminderGap: reminderGap,
      reminderEnabled: reminderEnabled.toString(),
    },
  });

  const onSubmit = useCallback(
    async (data: CampaignFormData) => {
      const insertedCampaign = await insertCampaign({
        variables: {
          input: {
            companyId,
            name: data.name,
            reminderEnabled: data.reminderEnabled === "true",
            reminderGap: data.reminderGap,
            description: data.description,
          },
        },
      });

      if (insertedCampaign.errors)
        addNotification({
          variables: {
            status: "error",
            title: "Erreur lors de la création de votre campagne",
          },
        });
      else {
        addNotification({
          variables: {
            status: "success",
            title: "Nouvelle campagne créé",
          },
        });
        onClose();
      }
    },
    [companyId, onClose]
  );

  useEffect(() => {
    register("reminderGap")
  }, [reminderEnabled]);

  const reminderSelector = useWatch({
    control: control,
    name: `reminderEnabled`,
  });


  return (
    <Modal isOpen={toggled} aria-label="Talent creation form">
      <ModalHeader onClose={onClose}>{t("mads.createCampaign")}</ModalHeader>
      <ModalBody>
        <form
          id="campaign-creation-form"
          className="flex flex-col pt-2 space-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Input
            name="name"
            ref={register({ required: "Une campagne doit avoir un nom" })}
            id="name"
            label={t("mads.nameLabel") as string}
            error={errors?.name}
          />
          <Textarea
            name="description"
            ref={register}
            id="description"
            label={"Description"}
          />
          <RadioGroup
            title={t("company.reminderEnabledLabel")}
            description={t("company.reminderEnabledDescription")}
          >
            <Radio
              id={`reminder-true`}
              label={t("forms.actions.yes")}
              name="reminderEnabled"
              value={"true"}
              ref={register}
            />
            <Radio
              id={`reminder-false`}
              label={t("forms.actions.no")}
              name="reminderEnabled"
              value={"false"}
              ref={register}
            />
          </RadioGroup>
          {reminderSelector === "true" && (
            <Input
              label={t("company.reminderGapLabel") as string}
              type="number"
              size="small"
              min={1}
              id="reminderGap"
              name="reminderGap"
              ref={register({ valueAsNumber: true })}
              defaultValue={reminderGap || undefined}
            />
          )}
        </form>
      </ModalBody>
      <ModalActions>
        <Button variant="subtle" onClick={onClose}>
          {t("forms.actions.cancel")}
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="campaign-creation-form"
          loading={loading}
        >
          {t("mads.createCampaign")}
        </Button>
      </ModalActions>
    </Modal>
  );
};
